import React from "react";
import { Container, Card, Alert, Badge, ListGroup } from "react-bootstrap";

/**
 * EMAlpha Data Deletion (Web + App)
 * - Works as a public website page AND as an in-app legal page
 * - Use the same component on /data-deletion and inside your app's "Legal" section
 *
 * Notes:
 * - Keep this page publicly accessible for Google Play "Data deletion URL"
 * - Keep a clear contact method (email) + required details
 */
export default function DataDeletion() {
  return (
    <Container className="py-4">
      <Card className="shadow-sm">
        <Card.Body>
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
            <h2 className="mb-0">EMAlpha – Account & Data Deletion</h2>
            <Badge bg="secondary">Last Updated: [01 Jan 2026]</Badge>
          </div>

          <p className="text-muted mb-3">
            This page explains how you can request deletion of your EMAlpha account
            and associated personal data for EMAlpha’s services and applications,
            including CoTrader and MacroMonitor (the “App”), as well as our website.
          </p>

          <Alert variant="info" className="mb-4">
            <strong>Quick option:</strong> Email{" "}
            <strong>support@emalpha.com</strong> with the subject{" "}
            <strong>“Data Deletion Request”</strong>.
          </Alert>

          <section className="mb-4">
            <h5 className="mb-2">How to Request Deletion</h5>
            <p className="mb-2">
              You can request deletion using either method below:
            </p>

            <ListGroup className="mb-3">
              <ListGroup.Item>
                <strong>Method 1 (Recommended): Email</strong>
                <div className="mt-2">
                  Send an email to <strong>support@emalpha.com</strong> from your
                  registered email address.
                </div>
              </ListGroup.Item>
            </ListGroup>

            <p className="mb-2">
              To help us verify and process your request, please include:
            </p>
            <ul className="mb-0">
              <li>Your registered email address</li>
              <li>Your full name (optional, if it helps identify your account)</li>
              <li>Your organization/company name (if applicable)</li>
              <li>Which product(s) you use: CoTrader, MacroMonitor, or both</li>
              <li>
                A clear statement: <em>“Please delete my account and associated personal data.”</em>
              </li>
            </ul>
          </section>

          <section className="mb-4">
            <h5 className="mb-2">What We Delete</h5>
            <p className="mb-2">
              Upon verification and approval of your request, we will delete or
              de-identify (where appropriate) personal data associated with your
              account, such as:
            </p>
            <ul className="mb-0">
              <li>Account profile information (e.g., email, name, organization)</li>
              <li>Authentication identifiers tied to your account</li>
              <li>User-associated app/website usage data, where reasonably identifiable</li>
              <li>Support tickets and communications (unless retention is required)</li>
              <li>Stored preferences and settings linked to your account</li>
            </ul>
          </section>

          <section className="mb-4">
            <h5 className="mb-2">What We May Retain</h5>
            <p className="mb-2">
              Certain data may be retained where necessary for legal, regulatory,
              security, fraud-prevention, or operational purposes. This may include:
            </p>
            <ul className="mb-0">
              <li>Records required to comply with applicable laws and regulations</li>
              <li>Billing, payment, invoice, and tax records (as required by law)</li>
              <li>Security logs and audit trails (retained for a limited period)</li>
              <li>
                Aggregated or anonymized data that cannot reasonably identify you
              </li>
            </ul>
          </section>

          <section className="mb-4">
            <h5 className="mb-2">Processing Time</h5>
            <p className="mb-0">
              We generally process verified deletion requests within{" "}
              <strong>30 days</strong>. In some cases, processing may take longer
              where required by law, for security review, or due to the complexity
              of the request.
            </p>
          </section>

          <section className="mb-4">
            <h5 className="mb-2">Verification & Unauthorized Requests</h5>
            <p className="mb-0">
              To protect users, we may request additional information to verify
              account ownership before deleting data. We may deny requests that we
              cannot verify or that are fraudulent or unlawful.
            </p>
          </section>

          <section>
            <h5 className="mb-2">Contact</h5>
            <p className="mb-0">
              For account and data deletion requests, contact:
              <br />
              <strong>EMAlpha LLC</strong>
              <br />
              📧 <strong>support@emalpha.com</strong>
            </p>
          </section>
        </Card.Body>
      </Card>
    </Container>
  );
}
